"use client";

import { FC, ReactNode, memo } from "react";
import {
  designClassMap,
  ButtonComponent,
  Color,
  Design,
  Type,
  Size,
  sizeClassMap,
} from "apps/website/components/base/Button/Button.map";
import { WithTestID } from "apps/website/types";
import cx from "apps/website/utils/misc/cx";
import { PublicConfig } from "apps/website/config/public";
import { PRODUCTION_URLS } from "apps/website/constants";
import { normaliseHref } from "apps/website/utils/links/href";

import { DisplayState } from "@/constants/state";

import Icon from "../Icon/Icon";
import TextControl from "../Text/TextControl/TextControl";
import { LinkWithQuery } from "../../overrides/LinkWithQuery/LinkWithQuery";

import ButtonState from "./ButtonState";

export interface IButton extends WithTestID {
  state?: DisplayState;
  id?: string;
  ariaControls?: string;
  ariaExpanded?: boolean;
  to?: string;
  type?: Type;
  size?: Size;
  design?: Design;
  color?: Color;
  disabled?: boolean;
  onClick?: () => void;
  forceHardReload?: boolean;
  className?: string;
  component?: string;
  children: ReactNode;
  withIcon?: boolean; // Relevant for links
  disableOnClick?: boolean;
}

export interface IButtonIcon {
  to?: string;
}

export interface IButtonAnchorTag {
  state: DisplayState;
  disabled?: boolean;
  children: ReactNode;
  withIcon?: boolean; // Relevant for links
  isExternalLink?: boolean;
  link?: string;
  classes: string;
  id?: string;
  testId?: string;
  onClick?: () => void;
  size?: Size;
  to?: string;
  component?: string;
  disableOnClick?: boolean;
}

export interface IWrappedOrUnwrappedButtonAnchorTag extends IButtonAnchorTag {
  wrapped: boolean;
  nextLink: string;
}

const isFrontendProject = (to?: string) => to?.startsWith("/manage-plan");

export const isFullPath = (to?: string) => (
  to?.startsWith(PublicConfig.NEXT_PUBLIC_WEBSITE_URL) ||
  !!PRODUCTION_URLS.find((url) => to?.startsWith(url))
);

const isLinkExternal = (to?: string) => {
  const startsWithHashOrQuery = to?.startsWith("#") || to?.startsWith("?");
  const isRelativePath = to?.startsWith("/") || startsWithHashOrQuery;
  return isFrontendProject(to)
    || (!isRelativePath && !isFullPath(to));
};

const ButtonIcon: FC<IButtonIcon> = ({ to }) => {
  if (!to) { return <></>; }
  return (
    <div className={cx("h-5 w-5 flex items-center justify-center ml-2")}>
      <Icon size="fit" icon={ to.startsWith("#") ? "arrowThinDown" : "arrowRightShort" } color="inherit" />
    </div>
  );
};

const AnchorTag: FC<IButtonAnchorTag> = ({
  state, disabled, children, withIcon, isExternalLink, link, classes, id, testId, onClick, size, to, component = "Button", disableOnClick = false,
}) => {
  const target = isExternalLink && !isFrontendProject(to) ? "_blank" : undefined;
  const rel = isExternalLink && !isFrontendProject(to) ? "nofollow noopener" : undefined;
  return (
    <a
      {...(!disableOnClick ? { onClick } : {})}
      data-component={ component }
      href={ link }
      data-disabled={ disabled }
      target={ target }
      rel={ rel }
      className={ classes }
      id={ id }
      data-testid={ testId }
    >
      <TextControl tag="span" size={ size === "large" ? 1 : 2 } color="inherit" className="flex justify-center items-center">
        <ButtonState state={ state }>{ children }{ withIcon ? <ButtonIcon to={ to } /> : null }</ButtonState>
      </TextControl>
    </a>
  );
};

const WrappedOrUnWrappedAnchorTag: FC<IWrappedOrUnwrappedButtonAnchorTag> = ({
  state,
  disabled,
  children,
  withIcon,
  isExternalLink,
  link,
  classes,
  id, testId,
  onClick,
  size,
  to,
  wrapped,
  nextLink,
  component,
  disableOnClick,
}) => (
  <>
    { wrapped ? (
      <LinkWithQuery href={ nextLink } legacyBehavior>
        <AnchorTag
          state={ state }
          disabled={ disabled }
          withIcon={ withIcon }
          isExternalLink={ isExternalLink }
          link={ link }
          classes={ classes }
          id={ id }
          testId={ testId }
          onClick={ onClick }
          size={ size }
          to={ to }
          component={ component }
          disableOnClick={disableOnClick}
        >
          { children }
        </AnchorTag>
      </LinkWithQuery>
    ) : (
      <AnchorTag
        state={ state }
        disabled={ disabled }
        withIcon={ withIcon }
        isExternalLink={ isExternalLink }
        link={ link }
        classes={ classes }
        id={ id }
        testId={ testId }
        onClick={ onClick }
        size={ size }
        to={ to }
        component={ component }
      >
        { children }
      </AnchorTag>
    ) }
  </>
);

const Button: FC<IButton> = ({
  color = "default",
  id,
  "data-testid": testId,
  ariaControls,
  ariaExpanded,
  state = DisplayState.READY,
  design = "default",
  to,
  type = "button",
  size = "default",
  disabled = false,
  onClick,
  className = "",
  component = "Button",
  forceHardReload = false,
  withIcon = false,
  children,
  disableOnClick = false,
}) => {

  const componentElement: ButtonComponent = to ? ButtonComponent.LINK : ButtonComponent.DEFAULT;
  const computedClasses = `${designClassMap[design].base} ${designClassMap[design][color]} ${sizeClassMap[size]} ${className}`;

  // Link — normalise before classifying so legacy signup / katkin.com hrefs
  // resolve to the final internal destination (TECH-3090).
  const normalisedTo = to ? normaliseHref(to) : to;
  const link = disabled ? "" : normalisedTo;
  const nextHref = link ?? "";
  const showUnwrappedAnchorTag = isLinkExternal(normalisedTo) || !normalisedTo || forceHardReload;

  return <>
    { componentElement === ButtonComponent.DEFAULT && (
      <button
        data-component={ component }
        id={ id }
        data-testid={ testId }
        type={ type }
        className={ computedClasses }
        disabled={ disabled }
        onClick={ onClick }
        aria-controls={ ariaControls }
        aria-expanded={ ariaExpanded }
      >
        <TextControl tag="span" size={ size === "large" ? 1 : 2 } color="inherit" align="center" className="flex justify-center items-center">
          <ButtonState state={ state }>{ children }</ButtonState>
        </TextControl>
      </button>
    ) }
    { componentElement === ButtonComponent.LINK && (
      <WrappedOrUnWrappedAnchorTag
        state={ state }
        disabled={ disabled }
        withIcon={ withIcon }
        isExternalLink={ isLinkExternal(normalisedTo) }
        link={ link }
        classes={ computedClasses }
        id={ id }
        testId={ testId }
        onClick={ onClick }
        size={ size }
        to={ normalisedTo }
        nextLink={ nextHref }
        wrapped={ !showUnwrappedAnchorTag }
        component={ component }
        disableOnClick={disableOnClick}
      >
        { children }
      </WrappedOrUnWrappedAnchorTag>
    ) }
  </>;
};

export default memo(Button);
